import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

const SCROLL_LOCK_ATTR = 'scroll-lock';
const SCROLL_LOCK_COUNT_DATA = 'scrollLockCount';
const SCROLL_LOCK_SCROLL_Y_DATA = 'scrollLockScrollY';

/**
 * Lock page scroll without layout shift (ref-counted).
 *
 * We fix the body at the current scroll position and compensate for scrollbar
 * removal by adding equivalent right padding. This prevents the "page jump"
 * when opening drawers/dialogs.
 *
 * Multiple components can request a lock; only the final unlock restores state.
 *
 * @param {number} [scrollY] - The current window.scrollY captured before locking.
 */
export function lockPageScroll(scrollY = window.scrollY) {
  const html = document.documentElement;
  const dataset = /** @type {DOMStringMap & Record<string, string>} */ (html.dataset);

  const currentCount = Number(dataset[SCROLL_LOCK_COUNT_DATA] || 0);
  if (currentCount === 0) {
    // Compute scrollbar width BEFORE applying overflow hidden / fixed positioning.
    const scrollbarWidth = Math.max(0, window.innerWidth - html.clientWidth);

    dataset[SCROLL_LOCK_SCROLL_Y_DATA] = String(scrollY);
    html.setAttribute(SCROLL_LOCK_ATTR, '');

    // Prevent layout shift by keeping the content width stable.
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.paddingRight = scrollbarWidth ? `${scrollbarWidth}px` : '';
  }

  dataset[SCROLL_LOCK_COUNT_DATA] = String(currentCount + 1);
}

/**
 * Unlock page scroll (ref-counted).
 */
export function unlockPageScroll() {
  const html = document.documentElement;
  const dataset = /** @type {DOMStringMap & Record<string, string>} */ (html.dataset);

  const currentCount = Number(dataset[SCROLL_LOCK_COUNT_DATA] || 0);
  if (!currentCount) return;

  const nextCount = currentCount - 1;

  if (nextCount > 0) {
    dataset[SCROLL_LOCK_COUNT_DATA] = String(nextCount);
    return;
  }

  // Final unlock: restore everything.
  const previousScrollY = Number(dataset[SCROLL_LOCK_SCROLL_Y_DATA] || 0);

  delete dataset[SCROLL_LOCK_COUNT_DATA];
  delete dataset[SCROLL_LOCK_SCROLL_Y_DATA];

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.paddingRight = '';

  html.removeAttribute(SCROLL_LOCK_ATTR);
  window.scrollTo({ top: previousScrollY, behavior: 'instant' });
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockPageScroll(scrollY);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockPageScroll();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          lockPageScroll();
        } else {
          unlockPageScroll();
        }
      }
    }
  },
  { capture: true }
);
